import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Versículos famosos como fallback si YouVersion no responde
const FALLBACK_VERSES = [
  { texto: 'Todo lo puedo en Cristo que me fortalece', referencia: 'Filipenses 4:13' },
  { texto: 'Porque yo sé los planes que tengo para ustedes, planes de bienestar y no de calamidad', referencia: 'Jeremías 29:11' },
  { texto: 'Jehová es mi pastor; nada me faltará', referencia: 'Salmos 23:1' },
  { texto: 'Confía en el Señor de todo corazón, y no en tu propia inteligencia', referencia: 'Proverbios 3:5' },
  { texto: 'El amor es paciente, es bondadoso. El amor no es envidioso', referencia: '1 Corintios 13:4' },
  { texto: 'Buscad primeramente el reino de Dios y su justicia', referencia: 'Mateo 6:33' },
  { texto: 'Porque de tal manera amó Dios al mundo, que ha dado a su Hijo unigénito', referencia: 'Juan 3:16' },
  { texto: 'El Señor es mi luz y mi salvación; ¿de quién temeré?', referencia: 'Salmos 27:1' },
  { texto: 'Echad toda vuestra ansiedad sobre él, porque él tiene cuidado de vosotros', referencia: '1 Pedro 5:7' },
  { texto: 'Si Dios es por nosotros, ¿quién contra nosotros?', referencia: 'Romanos 8:31' },
];

// Strip HTML tags for plain text display (the API may return HTML even in text mode)
const stripHtml = (s = '') => String(s).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

// Format "reference" object from YouVersion. It can be a string or an object
// like { human: "Salmo 23:3", usfm: "PSA.23.3" }
const formatReference = (ref) => {
  if (!ref) return '';
  if (typeof ref === 'string') return ref;
  return ref.human || ref.human_long || ref.usfm || '';
};

const VersiculoWidgetCuadrado = () => {
  const navigate = useNavigate();
  const [versiculo, setVersiculo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('fallback'); // 'youversion' | 'fallback'

  useEffect(() => {
    let cancelled = false;
    const loadVotd = async () => {
      try {
        const res = await axios.get(`${API}/youversion/verse-of-the-day`);
        if (cancelled) return;
        const d = res.data || {};
        const text = stripHtml(d.text);
        const reference = formatReference(d.reference);
        if (text && reference) {
          setVersiculo({ texto: text, referencia: reference, passage_id: d.passage_id });
          setSource('youversion');
          setLoading(false);
          return;
        }
        throw new Error('VOTD payload sin texto/referencia');
      } catch (e) {
        // Fallback: versículo deterministico basado en el día del año
        if (cancelled) return;
        const hoy = new Date();
        const diaDelAno = Math.floor((hoy - new Date(hoy.getFullYear(), 0, 0)) / 86400000);
        const indice = diaDelAno % FALLBACK_VERSES.length;
        setVersiculo(FALLBACK_VERSES[indice]);
        setSource('fallback');
        setLoading(false);
      }
    };
    loadVotd();
    return () => { cancelled = true; };
  }, []);

  const handleClick = () => {
    // Si tenemos passage_id de YouVersion, abrir el lector YouVersion;
    // si no, fallback a /app/biblia.
    if (versiculo?.passage_id) {
      navigate('/app/biblia-youversion');
    } else {
      navigate('/app/biblia');
    }
  };

  if (loading || !versiculo) {
    return (
      <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-rose-600 rounded-2xl p-6 h-56 flex items-center justify-center">
        <i className="fas fa-spinner fa-spin text-white text-4xl" />
      </div>
    );
  }

  return (
    <div
      onClick={handleClick}
      className="group relative bg-gradient-to-br from-purple-500 via-pink-500 to-rose-600 rounded-2xl p-6 cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden h-56"
      data-testid="versiculo-widget"
    >
      {/* Efecto de brillo animado */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      {/* Contenido */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-white px-3">
        {/* Ícono */}
        <div className="mb-2 transform group-hover:scale-110 transition-transform duration-300">
          <i className="fas fa-bible text-3xl drop-shadow-lg" />
        </div>

        {/* Texto del versículo */}
        <div className="text-center flex-1 flex flex-col justify-center">
          <p className="text-xs md:text-sm font-medium italic leading-snug drop-shadow-md mb-2 line-clamp-4" data-testid="versiculo-texto">
            "{versiculo.texto}"
          </p>

          {/* Referencia */}
          <div className="flex items-center justify-center gap-2">
            <div className="h-px w-6 bg-white/50" />
            <p className="text-xs font-bold drop-shadow-md" data-testid="versiculo-referencia">
              {versiculo.referencia}
            </p>
            <div className="h-px w-6 bg-white/50" />
          </div>
        </div>

        {/* Título en la parte inferior */}
        <div className="mt-2 text-center">
          <p className="text-xs font-semibold opacity-90">
            📖 Versículo del Día{source === 'youversion' ? ' · YouVersion' : ''}
          </p>
          {/* Indicador de clic */}
          <p className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-1">
            <i className="fas fa-arrow-right mr-1" />
            {source === 'youversion' ? 'Abrir en YouVersion' : 'Ir a la Biblia'}
          </p>
        </div>
      </div>

      {/* Decoración de fondo */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12" />

      {/* Cruz decorativa */}
      <div className="absolute top-4 right-4 opacity-20">
        <i className="fas fa-cross text-4xl text-white" />
      </div>
    </div>
  );
};

export default VersiculoWidgetCuadrado;
